/// Port of mobile/lib/user/ui/auto_username_prompt_host.dart.

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import { useAuthStore } from '@/auth/handler/authStore';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { useGuestScoreMergeStore } from '@/user/handler/guestScoreMergeStore';
import { useAutoUsernamePromptStore } from '@/user/handler/autoUsernamePromptStore';

export function AutoUsernamePromptHost() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const visible = useAutoUsernamePromptStore((s) => s.visible);
  const mergeBusy = useGuestScoreMergeStore((s) => s.offer !== null);
  const consider = useAutoUsernamePromptStore((s) => s.consider);
  const changeNow = useAutoUsernamePromptStore((s) => s.changeNow);
  const dontAskAgain = useAutoUsernamePromptStore((s) => s.dontAskAgain);
  const notNow = useAutoUsernamePromptStore((s) => s.notNow);

  useEffect(() => {
    consider(user.id, user.username, user.isAnonymous);
  }, [consider, user.id, user.username, user.isAnonymous]);

  useEffect(() => {
    if (!visible) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  const suppress =
    location.pathname === appRoutes.auth ||
    location.pathname === appRoutes.changeUsername ||
    location.pathname.startsWith(appRoutes.resetPassword);

  if (!visible || suppress || mergeBusy) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-6"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-sm">
        <PixelContainer
          radius={4}
          pixelScale={2}
          padding="24px 20px 16px"
          backgroundColor={colors.surface}
          borderColor={colors.onSurface}
          className="w-full"
        >
          <h2 className="text-center leading-tight" style={{ fontSize: 28, color: colors.onSurface }}>
            {t('autoUsernamePromptTitle')}
          </h2>
          <p
            className="mt-3 text-center"
            style={{ fontSize: 16, lineHeight: 1.35, color: colors.onSurface, opacity: 0.75 }}
          >
            {t('autoUsernamePromptMessage', { username: user.username })}
          </p>
          <div className="mt-5 flex flex-col gap-2.5">
            <PixelContainer
              radius={3}
              pixelScale={2}
              padding="12px 0"
              backgroundColor={colors.onSurface}
              borderColor={colors.onSurface}
              className="w-full"
              onClick={() => {
                changeNow(user.id);
                navigate(appRoutes.changeUsername);
              }}
            >
              <div className="flex justify-center">
                <span className="leading-none" style={{ fontSize: 20, color: colors.surface }}>
                  {t('autoUsernamePromptChangeNow')}
                </span>
              </div>
            </PixelContainer>
            <PixelContainer
              radius={3}
              pixelScale={2}
              padding="12px 0"
              backgroundColor={colors.surfaceContainerHighest}
              borderColor={colors.onSurface}
              className="w-full"
              onClick={() => dontAskAgain(user.id)}
            >
              <div className="flex justify-center">
                <span className="leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
                  {t('autoUsernamePromptDontAsk')}
                </span>
              </div>
            </PixelContainer>
            <PixelContainer
              radius={3}
              pixelScale={2}
              padding="12px 0"
              backgroundColor={colors.surfaceContainerHighest}
              borderColor={colors.onSurface}
              className="w-full"
              onClick={() => notNow(user.id)}
            >
              <div className="flex justify-center">
                <span className="leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
                  {t('autoUsernamePromptNotNow')}
                </span>
              </div>
            </PixelContainer>
          </div>
        </PixelContainer>
      </div>
    </div>
  );
}
